import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MapPicker from "@/components/MapPicker";
import { compressImage } from "@/lib/image";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, MapPin, ChatCircleDots, Camera } from "@phosphor-icons/react";

export default function ScanPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doudou, setDoudou] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [pos, setPos] = useState(null);
  const [form, setForm] = useState({ finder_first_name: "", finder_email: "", initial_message: "", photo: null });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/doudou/${id}`);
        setDoudou(data);
      } catch { setNotFound(true); }
    })();
  }, [id]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Photo trop lourde (max 15 Mo)"); return; }
    try {
      setForm((f) => ({ ...f, photo: null }));
      const compressed = await compressImage(file);
      setForm((f) => ({ ...f, photo: compressed }));
    } catch { toast.error("Impossible de lire cette photo"); }
  };

  const startChat = async () => {
    if (!form.photo) { toast.error("La photo du doudou trouvé est obligatoire"); return; }
    if (!form.finder_first_name || !form.finder_email || !form.initial_message) { toast.error("Prénom, email et message requis"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/public/doudous/${id}/contact`, {
        ...form,
        lat: pos?.lat, lng: pos?.lng, location_label: ""
      });
      localStorage.setItem(`finder_token_${data.conversation_id}`, data.finder_token);
      localStorage.setItem("finder_report_token", data.finder_token);
      toast.success("Message envoyé à la famille !");
      navigate(`/conversations/${data.conversation_id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur d'envoi, vérifiez les champs");
    } finally { setSending(false); }
  };

  if (notFound) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center" data-testid="scan-not-found">
          <h1 className="font-display text-3xl font-black text-stone-800">Doudou introuvable</h1>
          <Link to="/trouve" className="inline-block mt-6 text-orange-600 font-bold underline">Signaler un doudou trouvé</Link>
        </div>
      </Layout>
    );
  }
  if (!doudou) return <Layout><div className="max-w-3xl mx-auto p-12">Chargement...</div></Layout>;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-gradient-to-br from-orange-500 to-rose-500 rounded-[2rem] p-8 text-white text-center shadow-xl shadow-rose-300/40">
          <Heart size={48} weight="fill" className="mx-auto" />
          <h1 className="font-display text-3xl font-black mt-3" data-testid="scan-title">Vous avez trouvé un doudou !</h1>
          <p className="mt-2 text-white/90">Merci infiniment. Prévenez la famille de <strong>{doudou.name}</strong> en quelques secondes.</p>
        </div>

        <div className="grid md:grid-cols-[180px_1fr] gap-6 mt-8 bg-white rounded-3xl border border-rose-100 p-6">
          <div className="aspect-square rounded-2xl overflow-hidden bg-rose-100 grid place-items-center">
            {doudou.photo ? <img src={doudou.photo} alt={doudou.name} className="w-full h-full object-contain" /> : <Heart size={60} weight="duotone" className="text-rose-300" />}
          </div>
          <div>
            <h2 className="font-display text-2xl font-bold text-stone-800" data-testid="scan-doudou-name">{doudou.name}</h2>
            <p className="text-stone-600 mt-2">{doudou.description}</p>
            <p className="text-stone-500 text-sm mt-2">Famille : {doudou.owner_first_name}</p>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-3xl border border-rose-100 p-8">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><ChatCircleDots size={22} weight="duotone" className="text-orange-500" />Contacter la famille</h3>
          <p className="text-sm text-stone-500 mt-1">Seul votre prénom sera visible. Aucune création de compte nécessaire. La famille sera prévenue par email.</p>

          <div className="space-y-4 mt-5">
            <div>
              <Label>Photo du doudou trouvé *</Label>
              <label className="mt-2 flex flex-col items-center justify-center w-full min-h-[10rem] rounded-2xl border-2 border-dashed border-rose-200 bg-rose-50/50 cursor-pointer hover:bg-rose-50 overflow-hidden" data-testid="scan-photo-upload">
                {form.photo ? <img src={form.photo} alt="aperçu" className="w-full max-h-[24rem] object-contain rounded-2xl" data-testid="scan-photo-preview" /> : <><Camera size={32} weight="duotone" className="text-rose-400" /><span className="text-sm text-stone-500 mt-2">Cliquez pour ajouter une photo</span></>}
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} data-testid="scan-photo-input" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Votre prénom *</Label>
                <Input value={form.finder_first_name} onChange={(e) => setForm({ ...form, finder_first_name: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="finder-name-input" placeholder="Marie" />
              </div>
              <div>
                <Label>Votre email * <span className="text-xs text-stone-400 font-normal">(privé)</span></Label>
                <Input type="email" value={form.finder_email} onChange={(e) => setForm({ ...form, finder_email: e.target.value })} className="rounded-2xl mt-1 py-6" data-testid="finder-email-input" placeholder="vous@exemple.fr" />
              </div>
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea rows={3} value={form.initial_message} onChange={(e) => setForm({ ...form, initial_message: e.target.value })} className="rounded-2xl mt-1" data-testid="finder-message-input" placeholder="J'ai trouvé votre doudou au parc..." />
            </div>
            <div>
              <Label><MapPin size={16} className="inline mr-1" /> Où l'avez-vous trouvé ? (optionnel)</Label>
              <div className="mt-2"><MapPicker value={pos} onChange={setPos} height={280} /></div>
            </div>
            <Button onClick={startChat} disabled={sending} className="w-full bg-orange-500 hover:bg-orange-600 rounded-full py-6 font-bold shadow-lg shadow-orange-500/30" data-testid="start-chat-button">
              {sending ? "Envoi..." : "Envoyer à la famille"}
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
